import { checkIntlSupportedCurrency } from './checkIntlSupportedCurrency'
import { FormatCurrencyParams } from './types'
import { isEmptyValue } from './utils'
import { getPrecision } from './getPrecision'
import { findCustomCurrency } from './customCurrencies'
import { numberToString } from '@/utils/exponential'

export const LONG_DASH = '—'
const minValue = 0.01

const isSmallNumber = (num: number) => {
  return num !== 0 && Math.abs(num) < minValue
}

interface TruncateNumberParams {
  maxPrecision: number
}

const truncateValue = (value: string, { maxPrecision }: TruncateNumberParams) => {
  // eslint-disable-next-line prefer-const
  let [intPart, decimalPart] = value.split('.')

  if (decimalPart && decimalPart.length > maxPrecision) {
    decimalPart = decimalPart.slice(0, maxPrecision)
  }

  if (!decimalPart) {
    return intPart
  }

  return `${intPart}.${decimalPart}`
}

const FRACTION_DIGITS_LIB_LIMIT = 20

// eslint-disable-next-line complexity
export const formatCurrency = (locale: string, params: FormatCurrencyParams) => {
  const {
    value,
    currency,
    full,
    isSignAlwaysDisplay,
    cutSmallNumber,
    extendedNumber,
    isCompact,
    maxPrecision,
    hideCurrency,
    isFiat = false,
  } = params

  if (isEmptyValue(value)) {
    return LONG_DASH
  }

  const numberValue = Number(value)

  const customCurrency = findCustomCurrency(currency)

  const useIntlCurrencyStyle = customCurrency?.useIntlCurrencyStyle ?? checkIntlSupportedCurrency(currency, locale)

  const opts: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    signDisplay: isSignAlwaysDisplay ? 'exceptZero' : 'auto',
    style: !hideCurrency && useIntlCurrencyStyle ? 'currency' : undefined,
    currency: !hideCurrency && useIntlCurrencyStyle ? currency : undefined,
    notation: isCompact ? 'compact' : undefined,
  }

  const shouldCutNumberDisplay = cutSmallNumber && isFiat && isSmallNumber(numberValue)

  if (shouldCutNumberDisplay) {
    const formatter = new Intl.NumberFormat(locale, opts)
    const isNegative = numberValue < 0
    let result = formatter.format(isNegative ? -minValue : minValue)

    if (!useIntlCurrencyStyle && !hideCurrency && currency) {
      const currencySign = customCurrency?.sign ?? currency
      result = `${result} ${currencySign}`
    }

    return result
  }

  let stringValue = numberToString(value)

  const maximumFractionDigits = getPrecision({
    value: stringValue,
    isFiat,
    maxPrecision,
    full,
  })

  opts.maximumFractionDigits = maximumFractionDigits

  stringValue = truncateValue(stringValue, { maxPrecision: maximumFractionDigits })

  const [intPart, decimalPart = ''] = stringValue.split('.')

  let needHighAccuracy = false
  if (opts.maximumFractionDigits > FRACTION_DIGITS_LIB_LIMIT) {
    opts.maximumFractionDigits = FRACTION_DIGITS_LIB_LIMIT
    if (decimalPart.length > FRACTION_DIGITS_LIB_LIMIT) {
      needHighAccuracy = true
    }
  }

  if (extendedNumber) {
    needHighAccuracy = false
    if (Math.abs(numberValue) < 0.0001) {
      opts.maximumFractionDigits = 8
      opts.minimumFractionDigits = 0
    } else if (Math.abs(numberValue) < 1) {
      opts.maximumFractionDigits = 4
      opts.minimumFractionDigits = 0
    } else if (Math.abs(numberValue) < 1e6) {
      opts.maximumFractionDigits = 2
      opts.minimumFractionDigits = 0
    }
  }

  let result
  if (needHighAccuracy) {
    // if value passed with very big decimal part

    const placeholderValue = Number(`${intPart}.1`)
    const formatter = new Intl.NumberFormat(locale, opts)
    const parts = formatter.formatToParts(placeholderValue)
    result = parts.reduce((acc, current) => {
      if (current.type === 'fraction' && decimalPart) {
        // remove trailing zeroes
        current.value = decimalPart.replace(/0+$/, '')
      }

      return acc + current.value
    }, '')
  } else {
    const formatter = new Intl.NumberFormat(locale, opts)

    try {
      // TS asserts that value should not be string, but it's possible according to mdn
      // so just in case
      result = formatter.format(stringValue as unknown as number)
    } catch (e) {
      result = formatter.format(Number(stringValue))
    }
  }

  if (!useIntlCurrencyStyle && !hideCurrency && currency) {
    const currencySign = customCurrency?.sign ?? currency
    return `${result} ${currencySign}`
  }

  return result
}
